// ============================================================
// The signed-in client's report view: branded header, client switcher,
// date range, and only the reports the portal exposes.
// ============================================================

import { useMemo, useState, type ComponentType } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
    Alert,
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    MenuItem,
    Select,
    Toolbar,
    Typography,
} from '@mui/material'
import { Logout } from '@mui/icons-material'

import { usePortalMeQuery } from '@/apis/portalApi'
import { pickClient, visiblePortalSections, type PortalSection } from '@/apis/portalTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { DateRangePicker } from '@/components/shared/DateRangePicker'
import { granularityForPeriod, periodToRange, todayIn, type Period } from '@/utils/dates'

import { PortalBrand, PoweredBy } from './PortalChrome'
import { portalPath, usePortal } from './portalContext'
import {
    BreakdownSection,
    EcommerceSection,
    EventsSection,
    GoalsSection,
    OverviewSection,
    PerformanceSection,
    RealtimeSection,
    RetentionSection,
    TimeseriesSection,
    type SectionProps,
} from './PortalSections'
import { portalSignedOut, sessionForPortal } from './portalSessionSlice'
import { brandingPalette } from './portalTheme'

const SECTIONS: Record<PortalSection, ComponentType<SectionProps>> = {
    overview: OverviewSection,
    timeseries: TimeseriesSection,
    realtime: RealtimeSection,
    breakdown: BreakdownSection,
    events: EventsSection,
    ecommerce: EcommerceSection,
    goals: GoalsSection,
    retention: RetentionSection,
    performance: PerformanceSection,
}

export function PortalReportsView() {
    const { portal: resolved, basePath } = usePortal()
    const session = sessionForPortal(
        useAppSelector(s => s.portalSession),
        resolved.id,
    )
    if (!session) {
        return <Navigate to={portalPath(basePath, 'login')} replace />
    }
    return <SignedInView />
}

function SignedInView() {
    const { portal: resolved, basePath } = usePortal()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const me = usePortalMeQuery()
    // /me has the freshest settings (reports may have changed since the page loaded).
    const portal = me.data?.portal ?? resolved
    const clients = me.data?.clients ?? []

    const [chosen, setChosen] = useState<string | null>(null)
    const [period, setPeriod] = useState<Period>('30d')
    const client = pickClient(clients, chosen)
    const interval = granularityForPeriod(period)
    const today = todayIn(portal.timezone)
    const args = useMemo(
        () => ({
            projectId: portal.project_id,
            subTenant: client,
            ...periodToRange(period, today),
        }),
        [portal.project_id, client, period, today],
    )
    const palette = brandingPalette(portal.branding)
    const colors = [palette.primary.main, palette.secondary.main]
    const sections = visiblePortalSections(portal.reports)
    const clientName = clients.find(c => c.external_id === client)?.name

    const signOut = () => {
        dispatch(portalSignedOut())
        navigate(portalPath(basePath, 'login'), { replace: true })
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="static" color="primary" elevation={0}>
                <Toolbar sx={{ gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
                        <PortalBrand inverse />
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    {clients.length > 1 && (
                        <Select
                            size="small"
                            value={client ?? ''}
                            onChange={e => setChosen(e.target.value)}
                            aria-label="Client"
                            sx={{
                                minWidth: 180,
                                bgcolor: 'background.paper',
                                '& .MuiSelect-select': { py: 0.75, fontSize: 14 },
                            }}
                        >
                            {clients.map(c => (
                                <MenuItem key={c.external_id} value={c.external_id}>
                                    {c.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    <Typography
                        variant="body2"
                        sx={{ display: { xs: 'none', md: 'block' }, opacity: 0.85 }}
                        noWrap
                    >
                        {me.data?.user.email}
                    </Typography>
                    <Button color="inherit" startIcon={<Logout />} onClick={signOut}>
                        Sign out
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
                {me.isLoading ? (
                    <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : me.error ? (
                    <Alert severity="error">{v2ErrorMessage(me.error)}</Alert>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Box>
                                <Typography variant="h5">{clientName ?? 'Your reports'}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {args.from === args.to
                                        ? args.from
                                        : `${args.from} – ${args.to}`}{' '}
                                    · {portal.timezone}
                                </Typography>
                            </Box>
                            <DateRangePicker value={period} onChange={setPeriod} />
                        </Box>
                        {client === null ? (
                            <Alert severity="info">
                                No reports are assigned to your login yet. Please contact your
                                account manager.
                            </Alert>
                        ) : (
                            <Box sx={{ display: 'grid', gap: 3 }}>
                                {sections.map(s => {
                                    const Section = SECTIONS[s]
                                    return (
                                        <Section
                                            key={s}
                                            args={args}
                                            interval={interval}
                                            currency={portal.currency || 'USD'}
                                            colors={colors}
                                        />
                                    )
                                })}
                            </Box>
                        )}
                    </>
                )}
            </Container>

            <Box component="footer" sx={{ textAlign: 'center', px: 2 }}>
                {portal.branding.supportEmail && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ pt: 2 }}
                    >
                        Questions about your reports?{' '}
                        <a href={`mailto:${portal.branding.supportEmail}`}>
                            {portal.branding.supportEmail}
                        </a>
                    </Typography>
                )}
                <PoweredBy show={portal.powered_by} />
            </Box>
        </Box>
    )
}
